
import { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCreateCustomFood, useUpdateCustomFood } from '@/api/food-library'
import type { FoodItem } from '@/types/diet-v2'

interface CustomFoodFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  food?: FoodItem // If provided, edit mode
  onSuccess?: () => void
}

export function CustomFoodForm({ open, onOpenChange, food, onSuccess }: CustomFoodFormProps) {
  const isEditMode = !!food

  const [formData, setFormData] = useState({
    name: food?.name || '',
    servingSize: food?.servingSize?.toString() || '',
    servingUnit: food?.servingUnit || 'g',
    calories: food?.calories?.toString() || '',
    protein: food?.macros.protein?.toString() || '',
    carbs: food?.macros.carbs?.toString() || '',
    fat: food?.macros.fat?.toString() || '',
    fiber: food?.macros.fiber?.toString() || '',
    description: food?.customData?.description || '',
    category: food?.customData?.category || '',
  })

  const createMutation = useCreateCustomFood()
  const updateMutation = useUpdateCustomFood()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const data = {
      name: formData.name,
      servingSize: parseFloat(formData.servingSize),
      servingUnit: formData.servingUnit,
      calories: parseInt(formData.calories),
      protein: parseFloat(formData.protein),
      carbs: parseFloat(formData.carbs),
      fat: parseFloat(formData.fat),
      fiber: formData.fiber ? parseFloat(formData.fiber) : undefined,
      description: formData.description || undefined,
      category: formData.category || undefined,
    }

    try {
      if (isEditMode && food) {
        await updateMutation.mutateAsync({ ...data, id: food._id })
      } else {
        await createMutation.mutateAsync(data)
      }
      onOpenChange(false)
      if (onSuccess) onSuccess()
      // Reset form
      setFormData({
        name: '',
        servingSize: '',
        servingUnit: 'g',
        calories: '',
        protein: '',
        carbs: '',
        fat: '',
        fiber: '',
        description: '',
        category: '',
      })
    } catch (error) {
      // Error is handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Custom Food' : 'Add Custom Food'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update the details of your custom food' : 'Create a new custom food for your gym'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Food Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Food Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Grilled Chicken Breast"
              required
            />
          </div>

          {/* Serving Size */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="servingSize">Serving Size *</Label>
              <Input
                id="servingSize"
                type="number"
                step="0.01"
                value={formData.servingSize}
                onChange={(e) => setFormData({ ...formData, servingSize: e.target.value })}
                placeholder="100"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="servingUnit">Unit *</Label>
              <Select value={formData.servingUnit} onValueChange={(v) => setFormData({ ...formData, servingUnit: v })}>
                <SelectTrigger id="servingUnit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="g">grams (g)</SelectItem>
                  <SelectItem value="oz">ounces (oz)</SelectItem>
                  <SelectItem value="ml">milliliters (ml)</SelectItem>
                  <SelectItem value="cup">cup</SelectItem>
                  <SelectItem value="tbsp">tablespoon (tbsp)</SelectItem>
                  <SelectItem value="tsp">teaspoon (tsp)</SelectItem>
                  <SelectItem value="piece">piece</SelectItem>
                  <SelectItem value="serving">serving</SelectItem>
                  <SelectItem value="scoop">scoop</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Calories */}
          <div className="space-y-2">
            <Label htmlFor="calories">Calories *</Label>
            <Input
              id="calories"
              type="number"
              value={formData.calories}
              onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
              placeholder="165"
              required
            />
          </div>

          {/* Macros */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="protein">Protein (g) *</Label>
              <Input
                id="protein"
                type="number"
                step="0.1"
                value={formData.protein}
                onChange={(e) => setFormData({ ...formData, protein: e.target.value })}
                placeholder="31"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="carbs">Carbs (g) *</Label>
              <Input
                id="carbs"
                type="number"
                step="0.1"
                value={formData.carbs}
                onChange={(e) => setFormData({ ...formData, carbs: e.target.value })}
                placeholder="0"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fat">Fat (g) *</Label>
              <Input
                id="fat"
                type="number"
                step="0.1"
                value={formData.fat}
                onChange={(e) => setFormData({ ...formData, fat: e.target.value })}
                placeholder="3.6"
                required
              />
            </div>
          </div>

          {/* Fiber */}
          <div className="space-y-2">
            <Label htmlFor="fiber">Fiber (g)</Label>
            <Input
              id="fiber"
              type="number"
              step="0.1"
              value={formData.fiber}
              onChange={(e) => setFormData({ ...formData, fiber: e.target.value })}
              placeholder="0"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="e.g., Protein, Supplements, Prepared Meals"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional details about this food..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : isEditMode
                  ? 'Update Food'
                  : 'Create Food'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
